/**
 * Gallery Module
 * Provides category data and API helpers for the gallery sidebar and content views.
 * Pulls categories from wiki data with sensible defaults.
 *
 * Usage:
 *   await GalleryModule.init();
 *   const categories = GalleryModule.getCategories();
 *   const calcs = await GalleryModule.getCalcsByCategory('finance');
 */

(function() {
  'use strict';

  // Force production API for actual data
  const API_BASE = 'https://instacalc.com';

  // Default categories - shown even before API data loads
  // Matches wiki-integration.js MAIN_CATEGORIES structure
  const DEFAULT_CATEGORIES = [
    {
      slug: 'finance',
      name: 'Finance',
      icon: 'coins',
      color: 'emerald',
      tags: ['finance', 'housing', 'investing', 'retirement', 'crypto', 'money', 'mortgage', 'loan', 'interest'],
      description: 'Loans, investments, budgeting'
    },
    {
      slug: 'health',
      name: 'Health',
      icon: 'heart-pulse',
      color: 'rose',
      tags: ['health', 'fitness', 'wellness', 'exercise', 'medical', 'bmi', 'calories'],
      description: 'BMI, calories, fitness tracking'
    },
    {
      slug: 'cooking',
      name: 'Cooking',
      icon: 'utensils',
      color: 'orange',
      tags: ['cooking', 'recipe', 'food', 'baking', 'kitchen'],
      description: 'Recipe scaling, conversions'
    },
    {
      slug: 'math',
      name: 'Math',
      icon: 'calculator',
      color: 'blue',
      tags: ['math', 'algebra', 'geometry', 'statistics', 'trigonometry'],
      description: 'General calculations'
    },
    {
      slug: 'science',
      name: 'Science',
      icon: 'flask',
      color: 'cyan',
      tags: ['science', 'chemistry', 'physics', 'engineering'],
      description: 'Physics, chemistry, engineering'
    },
    {
      slug: 'conversion',
      name: 'Conversion',
      icon: 'arrows-rotate',
      color: 'purple',
      tags: ['conversion', 'units', 'metric', 'imperial', 'temperature'],
      description: 'Unit conversions'
    },
    {
      slug: 'gaming',
      name: 'Gaming',
      icon: 'gamepad',
      color: 'violet',
      tags: ['gaming', 'rpg', 'dnd', 'video games', 'tabletop'],
      description: 'D&D, game mechanics'
    },
    {
      slug: 'other',
      name: 'More',
      icon: 'ellipsis',
      color: 'slate',
      tags: [],
      description: 'Miscellaneous calculators'
    }
  ];

  // State
  let categories = [];
  let calcs = [];
  let initialized = false;
  let loadPromise = null;

  /**
   * Initialize the gallery module
   * Loads categories and calculator data
   */
  async function init() {
    if (initialized) return;

    // Start with defaults if empty
    if (categories.length === 0) {
      categories = JSON.parse(JSON.stringify(DEFAULT_CATEGORIES));
      categories.forEach(c => { c.count = 0; c.items = []; });
    }

    // Load data in background (or join existing promise)
    if (!loadPromise) {
      loadPromise = loadData();
    }
    
    await loadPromise;
    initialized = true;
  }

  /**
   * Load calculator data from API or WikiDB
   */
  async function loadData() {
    try {
      // Try WikiDB first (fast, offline-capable)
      if (window.WikiDB) {
        const wikiDB = new window.WikiDB();
        await wikiDB.init();
        const cachedCalcs = await wikiDB.getCalculators();

        if (cachedCalcs && cachedCalcs.length > 0) {
          console.log('[Gallery] Loaded', cachedCalcs.length, 'calcs from WikiDB');
          calcs = transformWikiCalcs(cachedCalcs);
          distributeCalcs(calcs);

          // Pre-fetch full calc data in background for instant previews
          if (window.prefetchGalleryCalcs) {
            setTimeout(() => window.prefetchGalleryCalcs(calcs, 15), 500);
          }
        }
      }

      // Also try API for fresh data
      const apiCalcs = await fetchFromAPI();
      if (apiCalcs && apiCalcs.length > 0) {
        console.log('[Gallery] Loaded', apiCalcs.length, 'calcs from API');
        calcs = apiCalcs;
        distributeCalcs(calcs);

        // Pre-fetch after API load too
        if (window.prefetchGalleryCalcs) {
          setTimeout(() => window.prefetchGalleryCalcs(calcs, 15), 500);
        }
      }
    } catch (e) {
      console.warn('[Gallery] Error loading data:', e);
    }
  }

  /**
   * Fetch calculators from the wiki API
   */
  async function fetchFromAPI() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${API_BASE}/api/wiki/listRecent?limit=200`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.calcs && data.calcs.length > 0) {
          return transformWikiCalcs(data.calcs);
        }
      }
    } catch (e) {
      console.warn('[Gallery] API fetch failed:', e);
    }
    return [];
  }

  /**
   * Transform wiki calc format to gallery format
   */
  function transformWikiCalcs(wikiCalcs) {
    return wikiCalcs.map(c => ({
      id: c.id || c.slug,
      slug: c.slug,
      title: c.name || c.title,
      description: c.description || '',
      rows: c.rows || [],
      tags: c.tags || [],
      category: c.category || guessCategory(c),
      views: c.views || c.stats?.views || 0,
      hearts: c.hearts || c.stats?.hearts || 0,
      previewRows: generatePreviewRows(c)
    }));
  }

  /**
   * Guess category from tags
   */
  function guessCategory(calc) {
    const calcTags = (calc.tags || []).map(t => t.toLowerCase());

    for (const cat of DEFAULT_CATEGORIES) {
      if (cat.slug === 'other') continue;
      const matches = calcTags.some(t => cat.tags.includes(t));
      if (matches) return cat.slug;
    }
    return 'other';
  }

  /**
   * Generate preview rows from calc rows array
   */
  function generatePreviewRows(calc) {
    // Rows are stored as array of {text: "..."} objects
    const rows = calc.rows || [];
    if (!Array.isArray(rows) || rows.length === 0) return [];

    // Get first 4 non-empty rows
    const nonEmptyRows = rows.filter(r => r && r.text && r.text.trim());

    return nonEmptyRows.slice(0, 4).map((row, i, arr) => {
      const text = row.text || '';
      // Simple parsing - look for = to split label/value
      const parts = text.split('=');
      const isResult = text.includes('//') || i === arr.length - 1;

      return {
        label: parts[0]?.trim() || text,
        value: parts[1]?.trim() || '',
        isResult
      };
    });
  }

  /**
   * Distribute calculators into categories
   */
  function distributeCalcs(calcList) {
    // Reset counts
    categories.forEach(c => { c.items = []; c.count = 0; });

    calcList.forEach(calc => {
      const cat = categories.find(c => c.slug === calc.category) ||
                  categories.find(c => c.slug === 'other');

      if (cat) {
        cat.items.push(calc);
        cat.count++;
      }
    });

    // Dispatch update event for listeners
    window.InstacalcEvents.dispatch(window.InstacalcEvents.GALLERY_UPDATED, {
      categories, calcs: calcList
    });
  }

  /**
   * Get all categories with counts
   */
  function getCategories() {
    return categories;
  }

  /**
   * Get category by slug
   */
  function getCategory(slug) {
    return categories.find(c => c.slug === slug);
  }

  /**
   * Get calculators by category
   */
  function getCalcsByCategory(categorySlug) {
    if (categorySlug === 'all') return calcs;
    const cat = categories.find(c => c.slug === categorySlug);
    return cat ? cat.items : [];
  }

  /**
   * Get calculator by slug
   */
  function getCalcBySlug(slug) {
    return calcs.find(c => c.slug === slug);
  }

  /**
   * Search calculators
   */
  function searchCalcs(query) {
    if (!query) return calcs;
    const q = query.toLowerCase();
    return calcs.filter(c =>
      c.title?.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q) ||
      c.tags?.some(t => t.toLowerCase().includes(q))
    );
  }

  /**
   * Get total calculator count
   */
  function getTotalCount() {
    return calcs.length;
  }

  /**
   * Check if gallery is loaded
   */
  function isLoaded() {
    return initialized;
  }

  /**
   * Force refresh from API
   */
  async function refresh() {
    initialized = false;
    loadPromise = null;
    await init();
  }

  // Export module
  window.GalleryModule = {
    init,
    getCategories,
    getCategory,
    getCalcsByCategory,
    getCalcBySlug,
    searchCalcs,
    getTotalCount,
    isLoaded,
    refresh,
    DEFAULT_CATEGORIES
  };

  function registerAlpineGallery() {
    if (!window.Alpine) return;

    Alpine.data('gallerySidebar', gallerySidebarComponent);
    Alpine.data('galleryContent', galleryContentComponent);
    Alpine.data('galleryPreviewModal', galleryPreviewModalComponent);

    if (!Alpine.store('gallery')) {
      Alpine.store('gallery', {
        previewCalc: null,
        previewMode: (window.SettingsManager ? window.SettingsManager.get('ui.galleryPreviewMode') : localStorage.getItem('gallery-preview-mode')) || 'panel', // 'panel' or 'modal'

        setPreviewMode(mode) {
          this.previewMode = mode;
          if (window.SettingsManager) {
            window.SettingsManager.set('ui.galleryPreviewMode', mode);
          } else {
            localStorage.setItem('gallery-preview-mode', mode);
          }
        },

        closePreview() {
          this.previewCalc = null;

          // Don't unmount the persistent manager - just hide the panel
          // The manager stays mounted for instant re-shows

          if (window.InstacalcEvents?.dispatch) {
            // Toggle the gallery panel off (SidebarManager will restore sidebar if auto-collapsed)
            window.InstacalcEvents.dispatch(window.InstacalcEvents.PANEL_OPEN, { panel: 'gallery' });
          } else {
            Alpine.store('ui').collapse?.();
          }
        },

        openPreview(calc) {
          this.previewCalc = calc;

          if (this.previewMode === 'panel') {
            // Option B: Slide Panel (Right Rail) – use event bus so SidebarManager handles collapse
            if (window.InstacalcEvents?.dispatch) {
              console.log('[Gallery] Dispatching PANEL_OPEN event with force:', true);
              window.InstacalcEvents.dispatch(window.InstacalcEvents.PANEL_OPEN, { panel: 'gallery', force: true });
            } else {
              Alpine.store('ui').open?.('gallery', true);
            }

            // Render calc using persistent manager (instant if pre-fetched)
            setTimeout(() => {
              const embed = document.querySelector('[x-ref="galleryPanelEmbed"]');
              if (embed && window.GalleryEmbedManager) {
                // Mount persistent root if not already
                window.GalleryEmbedManager.mount(embed);
                // Show calc (uses cache, no remount)
                window.GalleryEmbedManager.showCalc(calc, { displayMode: true })
                  .then(() => {
                    console.log('[Gallery Panel] Calc shown:', calc.slug || calc.id);
                  })
                  .catch(err => {
                    console.error('[Gallery Panel] Show failed:', err);
                    embed.innerHTML = '<p class="p-4 text-theme-text-muted">Failed to load preview</p>';
                  });
              } else if (embed && window.renderGalleryCalc) {
                // Fallback to old method
                embed.innerHTML = '';
                window.renderGalleryCalc(calc, embed, { displayMode: true });
              }
            }, 50); // Reduced delay since no heavy remount
          } else {
            // Option A: Center Modal
            // Close panel if open
            Alpine.store('ui').showPanel = false;
            if (Alpine.store('ui').activePanel === 'gallery') {
              Alpine.store('ui').activePanel = null;
            }

            // Dispatch event for modal
            window.InstacalcEvents.dispatch(window.InstacalcEvents.GALLERY_PREVIEW, calc);
          }
        },

        shareCalc() {
          if (this.previewCalc?.slug) {
            const url = window.location.origin + '/' + this.previewCalc.slug;
            if (navigator.share) {
              navigator.share({
                title: this.previewCalc.title,
                text: this.previewCalc.description || 'Check out this calculator!',
                url: url
              });
            } else if (navigator.clipboard) {
              navigator.clipboard.writeText(url);
              console.log('[Gallery] Copied link:', url);
            }
          }
        }
      });
    }
  }

  if (window.Alpine) {
    registerAlpineGallery();
  }
  document.addEventListener('alpine:init', registerAlpineGallery);

  /**
   * Alpine component for gallery sidebar
   */
  function gallerySidebarComponent() {
    return {
      categories: [],
      activeCategory: null,
      loading: true,

      async init() {
        await GalleryModule.init();
        this.categories = GalleryModule.getCategories().filter(c => c.count > 0);
        this.loading = false;

        // Check URL for active category (/gallery/:category)
        const path = window.location.pathname;
        const match = path.match(/^\/gallery\/([^/?]+)/);
        if (match) {
          const slug = match[1];
          const validCategories = window.Router?.GALLERY_CATEGORIES || DEFAULT_CATEGORIES.map(c => c.slug);
          if (validCategories.includes(slug)) {
            this.activeCategory = slug;
          }
        }

        // Listen for updates
        window.InstacalcEvents.on(window.InstacalcEvents.GALLERY_UPDATED, () => {
          this.categories = GalleryModule.getCategories().filter(c => c.count > 0);
        });

        // Listen for category changes from content view
        window.InstacalcEvents.on(window.InstacalcEvents.GALLERY_CATEGORY_CHANGED, (e) => {
          this.activeCategory = e.detail.slug;
        });
      },

      selectCategory(slug) {
        this.activeCategory = slug;
        // Update URL without reload (/gallery/:category)
        history.pushState({}, '', '/gallery/' + slug);

        // Update Alpine store to trigger layout change (home -> gallery)
        if (typeof Alpine !== 'undefined' && Alpine.store) {
          if (!window.Router?.VIEW?.GALLERY) {
            throw new Error('Router.VIEW.GALLERY not defined - check script order');
          }
          Alpine.store('view', window.Router.VIEW.GALLERY);
        }

        // Load any newly visible templates (gallery-content.html) after DOM updates
        setTimeout(() => {
          if (typeof window.loadHTML === 'function') {
            window.loadHTML();
          }
        }, 50);

        // Notify content component
        window.InstacalcEvents.dispatch(window.InstacalcEvents.GALLERY_CATEGORY_CHANGED, { slug });
      }
    };
  }

  /**
   * Alpine component for gallery content view
   */
  function galleryContentComponent() {
    return {
      loading: true,
      title: 'Calculator Gallery',
      description: 'Browse and discover shareable calculators',
      activeCategory: 'all',
      searchQuery: '',
      calcs: [],
      hasMore: false,
      page: 1,

      get categories() {
        return GalleryModule.getCategories().filter(c => c.count > 0);
      },

      get filteredCalcs() {
        let results = this.calcs;

        if (this.activeCategory !== 'all') {
          results = results.filter(c => c.category === this.activeCategory);
        }

        if (this.searchQuery) {
          results = GalleryModule.searchCalcs(this.searchQuery);
          if (this.activeCategory !== 'all') {
            results = results.filter(c => c.category === this.activeCategory);
          }
        }

        return results;
      },

      async init() {
        await GalleryModule.init();
        this.calcs = GalleryModule.getCalcsByCategory('all');
        this.loading = false;

        // Check URL for category filter (/gallery/:category)
        const path = window.location.pathname;
        const match = path.match(/^\/gallery\/([^/?]+)/);
        if (match) {
          const slug = match[1];
          const validCategories = window.Router?.GALLERY_CATEGORIES || DEFAULT_CATEGORIES.map(c => c.slug);
          if (validCategories.includes(slug)) {
            this.activeCategory = slug;
            const cat = GalleryModule.getCategory(slug);
            if (cat) {
              this.title = cat.name;
              this.description = cat.description;
            }
          }
        }

        // Listen for updates
        window.InstacalcEvents.on(window.InstacalcEvents.GALLERY_UPDATED, () => {
          this.calcs = GalleryModule.getCalcsByCategory('all');
        });

        // Listen for category changes from sidebar
        window.InstacalcEvents.on(window.InstacalcEvents.GALLERY_CATEGORY_CHANGED, (e) => {
          this._setCategory(e.detail.slug, false); // Don't dispatch again
        });
      },

      // Internal method - skipDispatch prevents infinite loops
      _setCategory(slug, dispatch = true) {
        this.activeCategory = slug;
        this.page = 1;

        // Update title
        if (slug === 'all') {
          this.title = 'Calculator Gallery';
          this.description = 'Browse and discover shareable calculators';
        } else {
          const cat = GalleryModule.getCategory(slug);
          if (cat) {
            this.title = cat.name;
            this.description = cat.description;
          }
        }

        // Dispatch event to sync sidebar (if not already responding to one)
        if (dispatch) {
          const url = slug === 'all' ? '/gallery' : `/gallery/${slug}`;
          history.pushState({}, '', url);
          window.InstacalcEvents.dispatch(window.InstacalcEvents.GALLERY_CATEGORY_CHANGED, { slug });
        }
      },

      setCategory(slug) {
        this._setCategory(slug, true);
      },

      // Open preview panel for a calc
      previewCalc(calc) {
        // Use the gallery store to open the right rail panel
        Alpine.store('gallery').openPreview(calc);
      }
    };
  }

  /**
   * Alpine component for gallery preview modal
   */
  function galleryPreviewModalComponent() {
    return {
      isOpen: false,
      calc: null,
      _modalManager: null,  // Separate manager instance for modal

      openPreview(calc) {
        this.calc = calc;
        this.isOpen = true;
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';

        // Render the live calculator after DOM updates
        this.$nextTick(() => {
          const container = this.$refs.embedCalc;
          const headerButtons = this.$refs.headerButtons;
          if (container) {
            container.classList.remove('loaded');

            // Use GalleryEmbedManager for instant loading (uses cache)
            if (window.GalleryEmbedManager) {
              // Create separate manager instance for modal
              if (!this._modalManager) {
                this._modalManager = Object.create(window.GalleryEmbedManager);
                this._modalManager.root = null;
                this._modalManager.footerRoot = null;
                this._modalManager.container = null;
              }

              this._modalManager.mount(container, headerButtons);
              this._modalManager.showCalc(calc, { displayMode: true })
                .then(() => {
                  container.classList.add('loaded');
                  console.log('[Gallery Modal] Calc shown:', calc.slug || calc.id);
                })
                .catch(err => {
                  console.error('[Gallery Modal] Show failed:', err);
                  container.innerHTML = '<p class="p-4 text-theme-text-muted">Failed to load calculator preview</p>';
                });
            } else if (window.renderGalleryCalc) {
              // Fallback
              container.innerHTML = '';
              if (headerButtons) headerButtons.innerHTML = '';
              window.renderGalleryCalc(calc, container, { displayMode: true, footerEl: headerButtons })
                .then(() => container.classList.add('loaded'));
            } else {
              container.innerHTML = '<p class="p-4 text-theme-text-muted">Preview not available</p>';
            }
          }
        });
      },

      close() {
        this.isOpen = false;
        document.body.style.overflow = '';

        // Don't unmount modal manager - keep it for instant re-open
        // Just hide the modal (Alpine handles visibility)

        const container = this.$refs.embedCalc;
        if (container) {
          container.classList.remove('loaded');
        }
      }
    };
  }

})();
